import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useToasts } from 'react-toast-notifications';
import ReactTooltip from 'react-tooltip';
import Box from 'common/components/box/Box';
import LoadingSpinner from 'common/components/loading/LoadingSpinner';
import { OverdueMaintenanceDashboardDto } from 'api/dashboard/dtos/OverdueMaintenanceDashboardDto';
import DashboardService from 'api/dashboard/DashboardService';
import dayjs from 'dayjs';
import { DATE_FORMAT_DEFAUT } from 'Config';
import { FiltersFormModel } from '../filters/FiltersView';
import { SelectValueLabel } from 'common/types/SelectValueLabel';
import KpiHeader from '../components/KpiHeader';
import WidgetMonthPicker from '../components/WidgetMonthPicker';
import useWidgetFilters from '../hooks/useWidgetFilters';
import { useDashboardVehicleScope, vehicleScopeCriteria } from '../hooks/useDashboardVehicleScope';
import useInfiniteReveal from '../components/useInfiniteReveal';
import openMaintenancesList from '../utils/openMaintenancesList';
import tableStyles from '../dashboardTable/DashboardTable.module.scss';
import WidgetNoData from '../components/WidgetNoData';
import styles from './OverdueMaintenances.module.scss';
import infiniteStyles from '../components/useInfiniteReveal.module.scss';

const PAGE_SIZE = 10;

type Props = {
	filters?: FiltersFormModel;
	vehicles?: SelectValueLabel[];
};

const OverdueMaintenances = (props: Props) => {
	const { t } = useTranslation();
	const { addToast } = useToasts();
	const [data, setData] = useState<OverdueMaintenanceDashboardDto[]>([]);
	const [isLoading, setIsLoading] = useState(false);
	const { dateFrom, dateTo, setDateRange } = useWidgetFilters('overdueMaintenances');
	const vehicleScope = vehicleScopeCriteria(useDashboardVehicleScope());

	useEffect(() => {
		let cancelled = false;
		const fetchData = async () => {
			setIsLoading(true);
			try {
				const criteria = {
					...(props.filters || {}),
					...vehicleScope,
					dateFrom,
					dateTo,
				};
				const result = await DashboardService.getOverdueMaintenances(criteria);
				if (!cancelled) {
					setData(result || []);
				}
			} catch {
				if (!cancelled) addToast(t('common.messages.error_load_info'), { appearance: 'error' });
			} finally {
				if (!cancelled) setIsLoading(false);
			}
		};
		fetchData();
		return () => { cancelled = true; };
	}, [props.filters, dateFrom, dateTo]);

	const { visibleItems: pageItems, sentinelRef, hasMore } = useInfiniteReveal(data, PAGE_SIZE);

	const navigateToMaintenance = (vehicleId: string, maintenanceId: string) => {
		window.open(`/vehicles/details/${vehicleId}/maintenance/details/${maintenanceId}`, '_blank');
	};

	const buildVehicleTooltip = (item: OverdueMaintenanceDashboardDto) =>
		[item.brand, item.model].filter(Boolean).join(' ') || undefined;

	const getTypeName = (item: OverdueMaintenanceDashboardDto) => {
		return item.typeIsSystem ? t(`common.maintenance_contract_type.${item.typeName}` as any) as string : item.typeName;
	};

	const getDelayBadge = (item: OverdueMaintenanceDashboardDto) => {
		if (item.overdueKm != null && item.overdueKm > 0) {
			return (
				<span className={`${styles.badge} ${styles.badgeRed}`}>
					{(t as any)('dashboard.maintenance_status_overdue_km', { count: item.overdueKm.toLocaleString() })}
				</span>
			);
		}
		return (
			<span className={`${styles.badge} ${styles.badgeRed}`}>
				{(t as any)('dashboard.maintenance_status_overdue_days', { count: item.overdueDays })}
			</span>
		);
	};

	const renderOptions = () => (
		<WidgetMonthPicker dateFrom={dateFrom} dateTo={dateTo} onChange={setDateRange} />
	);

	return (
		<Box
			className={tableStyles.container}
			title={t('dashboard.overdue_maintenances') as string}
			renderOptions={renderOptions()}
			subTitleTooltip={t('dashboard.overdue_maintenances_tooltip' as any) as string}
		>
			{isLoading ? (
				<div className={tableStyles.centerAlign}>
					<LoadingSpinner />
				</div>
			) : data.length === 0 ? (
				<WidgetNoData />
			) : (
				<>
					<KpiHeader
						value={String(data.length)}
						subLabel={t('dashboard.total_overdue' as any) as string}
						currentTotal={data.length}
						previousTotal={null}
						previousRange={null}
						onClick={() => {
							const now = new Date();
							const cappedDateTo = dateTo && dateTo > now ? now : dateTo;
							openMaintenancesList({
								dateFrom,
								dateTo: cappedDateTo,
								dateField: 'expected',
								currentState: 'PREDICTED',
								currentStateName: t('maintenances.states.PREDICTED') as string,
							});
						}}
					/>
					<div className={styles.tableWrapper}>
						<table className={styles.table}>
							<thead>
								<tr>
									<th>{t('dashboard.columns.vehicle')}</th>
									<th>{t('dashboard.maintenance_columns.type' as any)}</th>
									<th>{t('dashboard.maintenance_columns.description' as any)}</th>
									<th>{t('dashboard.maintenance_columns.expected_date' as any)}</th>
									<th>{t('dashboard.maintenance_columns.delay' as any)}</th>
								</tr>
							</thead>
							<tbody>
								{pageItems.map((item, index) => (
									<tr
										key={`${item.maintenanceId}-${index}`}
										className={styles.tableRow}
										onClick={() => navigateToMaintenance(item.vehicleId, item.maintenanceId)}
									>
										<td><span className={styles.plate}><span data-tip={buildVehicleTooltip(item)} data-for="overdue-maintenances-vehicle-tooltip" data-place="right">{item.registrationNumber}</span></span></td>
										<td>{getTypeName(item)}</td>
										<td>{item.description || <span className={styles.muted}>—</span>}</td>
										<td>{item.expectedDate ? dayjs(item.expectedDate).format(DATE_FORMAT_DEFAUT) : <span className={styles.muted}>—</span>}</td>
										<td>{getDelayBadge(item)}</td>
									</tr>
								))}
								{hasMore && (
									<tr ref={sentinelRef as unknown as React.Ref<HTMLTableRowElement>}>
										<td colSpan={5} className={infiniteStyles.sentinelRow} />
									</tr>
								)}
							</tbody>
						</table>
					</div>
					<ReactTooltip id="overdue-maintenances-vehicle-tooltip" className={styles.tooltip} effect="solid" multiline={true} />
				</>
			)}
		</Box>
	);
};

export default OverdueMaintenances;
